import { dom, actions } from '@omegagrid/core';
import { Form, FormContainer } from "@omegagrid/form";
import rp, { AppComponent, Application, ParsedComponentId, GridEditor } from '@reporting/rp-app-host';
import { Sidebar, SecurityRolesContainer } from './components';
import {
	AccessTokenGridEditorAdapter, ReportGridEditorAdapter, AccessTokenFormAdapter, EmailGridEditorAdapter, ReportEmailFormAdapter,
	ClientCredentialsGridEditorAdapter, ClientCredentialsFormAdapter, ClientCredentialsFormVariant
} from './adapters'
import { ControlEvent } from '@omegagrid/form/dist/ui/controls/baseControl';
import { ReportEmail } from '@reporting/rp-types/accounting';
import { Application as ApplicationEntity } from '@reporting/rp-types/core';

const createMainSidebar = (app: Application, componentId: ParsedComponentId) => {
	const sidebar = dom.createElement<Sidebar>('rp-admin-sidebar');
	return sidebar;
}

const createSecurityRolesContainer = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<SecurityRolesContainer>('rp-admin-securityroles-container');
	return component;
}

const createReportGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ReportGridEditorAdapter();
	component.addEventListener('editor.action', (e: actions.ActionEvent) => {
		if (e.action.key === 'click') window.open(`${rp.options.clientUrl}/embed/${e.action.value}`, '_blank');
	});
	return component;
}

const createAccessTokenGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new AccessTokenGridEditorAdapter();
	return component;
}

export const createAccessTokenForm = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new AccessTokenFormAdapter();
	component.addEventListener('change', (e: ControlEvent<Form>) => {
		if (e.model.props.key === '_expiration') {
			(component.adapter as AccessTokenFormAdapter).updateFormControls(component.forms[0]);
		}
	});
	component.addEventListener('form.save', () => {
		try {
			(app.host.components.get('admin-accessTokens') as GridEditor).refresh();
		} catch {}
	});
	return component as unknown as AppComponent;
}

export const createEmailGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new EmailGridEditorAdapter();
	return component;
}

export const createEmailForm = async (app: Application, componentId: ParsedComponentId) => {
	const a = parseInt(componentId.ids[1]);
	if (isNaN(a)) throw new Error('Missing accounting in component ID');
	const idz = componentId.ids[2] ? parseInt(componentId.ids[2]) : null;
	const copy = componentId.ids[3] === 'copy';
	let email: ReportEmail = null;
	if (idz) {
		email = await rp.ms('accounting').get<ReportEmail>(`auto/emails/${idz}`, { accounting: a });
		if (copy) email.idz = null;
	}
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new ReportEmailFormAdapter(a, email);
	component.addEventListener('change', (e: ControlEvent<Form>) => {
		if (e.model.props.key === 'freq_type') {
			(component.adapter as ReportEmailFormAdapter).updateFormControls(component.forms[0]);
		}
	});
	component.addEventListener('form.save', () => {
		try {
			(app.host.components.get('admin-emails') as GridEditor).refresh();
		} catch {}
	});
	return component as unknown as AppComponent;
}

const createClientCredentialsGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ClientCredentialsGridEditorAdapter();
	return component;
}

/**
 * admin-clientCredentialsForm-{company|group}[-{idz}-{accounting}]: a registration of the given
 * binding, or the edit form of the registration idz (read back under its home company accounting).
 */
export const createClientCredentialsForm = async (app: Application, componentId: ParsedComponentId) => {
	const variant: ClientCredentialsFormVariant = componentId.ids[1] === 'group' ? 'group' : 'company';
	const idz = componentId.ids[2] ? parseInt(componentId.ids[2]) : null;
	const accounting = componentId.ids[3] ? parseInt(componentId.ids[3]) : null;
	const application = idz
		? await rp.ms('accounting').get<ApplicationEntity>(`clientCredentials/${idz}`, { accounting })
		: null;
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new ClientCredentialsFormAdapter(variant, application);
	component.addEventListener('form.save', () => {
		try {
			(app.host.components.get('admin-clientCredentials') as GridEditor).refresh();
		} catch {}
	});
	return component as unknown as AppComponent;
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['admin', createMainSidebar],
	['securityRoles', createSecurityRolesContainer],
	['reports', createReportGridEditor],
	['accessTokens', createAccessTokenGridEditor],
	['accessTokenForm', createAccessTokenForm],
	['emails', createEmailGridEditor],
	['emailForm', createEmailForm],
	['clientCredentials', createClientCredentialsGridEditor],
	['clientCredentialsForm', createClientCredentialsForm],
]);

